/**
 * 与 super-admin 父窗口的通信桥。
 *
 * 选 iframe 而非微前端：父子完全独立的 window / CSS / JS 运行时，不存在沙箱劫持，
 * 也就没有「沙箱吞掉异常 → 白屏且堆栈指向框架内部」这条故障路径。
 * 代价是通信必须显式走 postMessage，本文件就是那份契约。
 */

/**
 * 父窗口来源白名单。收到消息先验 origin，不在名单内一律丢弃。
 *
 * 必须与 web/public/_headers 里的 frame-ancestors 保持一致——
 * 那边控制"谁能嵌我"，这边控制"我信谁发来的消息"，少配一处就联调失败。
 */
const TRUSTED_PARENT_ORIGINS: readonly string[] = [
  "https://pb-super-admin-test.pages.dev", // test
  "https://pb-super-admin.pwtk.cc", // dev
  "https://super-admin.xcarefree.com", // 生产
  "http://localhost:3000", // 本地开发
];

export type ToParent =
  /** 子应用已挂载。父窗口据此清掉 loading 遮罩、停掉超时兜底 */
  | { type: "admin:ready"; route: string }
  /** 内容高度变化。父窗口据此调 iframe 高度，避免出现双滚动条 */
  | { type: "admin:height"; height: number }
  /** 子应用内部导航。父窗口据此同步菜单高亮与浏览器地址栏 */
  | { type: "admin:navigated"; route: string }
  /** 子应用崩了。父窗口据此显示重试按钮，而不是让用户对着白屏 */
  | { type: "admin:error"; message: string; stack?: string }
  /** 有未保存改动。父窗口据此在切菜单前拦截 */
  | { type: "admin:dirty"; dirty: boolean };

export type FromParent =
  /** 父窗口下发身份。token 不放 URL——URL 会进各级访问日志 */
  | { type: "parent:auth"; token: string }
  /** 父窗口点了菜单，要求子应用跳转 */
  | { type: "parent:navigate"; route: string }
  /** 父窗口主题切换 */
  | { type: "parent:theme"; theme: "light" | "dark" };

/**
 * super-admin 用的是 wujie。它有两种运行模式，本文件两种都支持：
 *
 *   默认模式  JS 跑在隐藏 iframe，DOM 渲染到主文档的 shadow DOM。
 *             白屏风险在这两者的连接线上——隐藏 iframe 的初始化与脚本注入
 *             存在时序竞争，接不上就是白屏，且跟机器快慢有关，所以「偶尔」。
 *   degrade   直接用真 iframe 承载，等价于本仓库的独立 iframe 方案。
 *
 * 被 wujie 承载时优先走它的 props / bus；否则回落到标准 postMessage。
 * 两条路径共用下面同一套 handler，业务代码无需区分。
 */
type WujieRuntime = {
  props?: { token?: string; route?: string };
  bus?: {
    $on(event: string, fn: (...args: unknown[]) => void): void;
    $off(event: string, fn: (...args: unknown[]) => void): void;
    $emit(event: string, ...args: unknown[]): void;
  };
};

function wujie(): WujieRuntime | null {
  const w = window as unknown as {
    __POWERED_BY_WUJIE__?: boolean;
    $wujie?: WujieRuntime;
  };
  return w.__POWERED_BY_WUJIE__ ? (w.$wujie ?? null) : null;
}

/** 当前是否被 wujie 承载。用于诊断，出问题时能一眼看出走的哪条路 */
export const HOSTED_BY_WUJIE = typeof window !== "undefined" && !!wujie();

let authToken: string | null = null;

export function getToken(): string | null {
  return authToken;
}

export function send(msg: ToParent): void {
  // 被 wujie 承载时走它的事件总线：shadow DOM 模式下父子同一个 document，
  // postMessage 到 window.parent 根本到不了 super-admin 的处理器。
  const bus = wujie()?.bus;
  if (bus) {
    bus.$emit(msg.type, msg);
    return;
  }

  if (window.parent === window) return; // 独立打开时无父窗口，静默跳过
  // 目标 origin 用具体值而非 "*"，避免把 token 相关状态广播给任意嵌入方
  for (const origin of TRUSTED_PARENT_ORIGINS) {
    try {
      window.parent.postMessage(msg, origin);
    } catch {
      /* origin 不匹配时浏览器会抛，属正常 */
    }
  }
}

type Handlers = {
  onAuth?: (token: string) => void;
  onNavigate?: (route: string) => void;
  onTheme?: (theme: "light" | "dark") => void;
};

export function listen(handlers: Handlers): () => void {
  const rt = wujie();
  if (rt) {
    // wujie 通过 props 同步下发首屏数据，不需要等握手
    if (rt.props?.token) {
      authToken = rt.props.token;
      handlers.onAuth?.(rt.props.token);
    }
    if (rt.props?.route) handlers.onNavigate?.(rt.props.route);

    const bus = rt.bus;
    if (!bus) return () => {};

    const onAuth = (...a: unknown[]) => {
      const token = (a[0] as { token?: string } | undefined)?.token;
      if (token) {
        authToken = token;
        handlers.onAuth?.(token);
      }
    };
    const onNav = (...a: unknown[]) => {
      const route = (a[0] as { route?: string } | undefined)?.route;
      if (route) handlers.onNavigate?.(route);
    };
    const onTheme = (...a: unknown[]) => {
      const theme = (a[0] as { theme?: "light" | "dark" } | undefined)?.theme;
      if (theme) handlers.onTheme?.(theme);
    };

    bus.$on("parent:auth", onAuth);
    bus.$on("parent:navigate", onNav);
    bus.$on("parent:theme", onTheme);
    return () => {
      bus.$off("parent:auth", onAuth);
      bus.$off("parent:navigate", onNav);
      bus.$off("parent:theme", onTheme);
    };
  }

  const onMessage = (e: MessageEvent) => {
    if (!TRUSTED_PARENT_ORIGINS.includes(e.origin)) return;
    const msg = e.data as FromParent;
    if (!msg || typeof msg.type !== "string") return;

    switch (msg.type) {
      case "parent:auth":
        authToken = msg.token;
        handlers.onAuth?.(msg.token);
        break;
      case "parent:navigate":
        handlers.onNavigate?.(msg.route);
        break;
      case "parent:theme":
        handlers.onTheme?.(msg.theme);
        break;
    }
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/**
 * 上报内容高度。
 *
 * 不用固定 100vh：iframe 内再出一条滚动条时，父页面滚到底也看不全内容，
 * 而且滚动位置在父子之间会打架。
 */
export function reportHeight(el: HTMLElement): () => void {
  const observer = new ResizeObserver(() => {
    send({ type: "admin:height", height: el.scrollHeight });
  });
  observer.observe(el);
  return () => observer.disconnect();
}

/**
 * 全局异常上报。
 *
 * 这是 iframe 相对微前端最实际的好处：子应用崩溃是**可观测**的——
 * 父窗口收到 admin:error 就能显示重试按钮，而不是留一片白。
 */
export function installErrorReporter(): void {
  window.addEventListener("error", (e) => {
    send({ type: "admin:error", message: e.message, stack: e.error?.stack });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    send({
      type: "admin:error",
      message: String(reason?.message ?? reason),
      stack: reason?.stack,
    });
  });
}
